"use client"

import { ChevronsUpDown, Plus } from "lucide-react"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from "@/components/ui/sidebar"
import { ENVIRONMENTS } from "@/config/navigation"
import { cn } from "@/utils/styles.helpers"

type EnvironmentValue = (typeof ENVIRONMENTS)[number]["value"]

const ENVIRONMENT_STYLES: Record<EnvironmentValue, string> = {
	production:
		"bg-green-100 border-green-300 text-green-700 dark:bg-green-950 dark:border-green-800 dark:text-green-400",
	staging:
		"bg-yellow-100 border-yellow-300 text-yellow-700 dark:bg-yellow-950 dark:border-yellow-800 dark:text-yellow-400",
	development:
		"bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-400",
	local:
		"bg-gray-100 border-gray-300 text-gray-700 dark:bg-gray-950 dark:border-gray-800 dark:text-gray-400",
} as const

/** On the indigo rail: the rail's own chip, with the environment in the icon's colour. */
const RAIL_ENVIRONMENT_STYLES: Record<EnvironmentValue, string> = {
	production: "border-transparent bg-rail-accent text-green-300",
	staging: "border-transparent bg-rail-accent text-yellow-300",
	development: "border-transparent bg-rail-accent text-sky-300",
	local: "border-transparent bg-rail-accent text-rail-accent-foreground",
} as const

function getEnvironmentClasses(
	environment: EnvironmentValue,
	onRail: boolean,
): string {
	return onRail
		? RAIL_ENVIRONMENT_STYLES[environment]
		: ENVIRONMENT_STYLES[environment]
}

function getCurrentEnvironment() {
	if (typeof window === "undefined") {
		return ENVIRONMENTS[0]
	}

	const currentHost = window.location.hostname
	const currentPort = window.location.port

	if (currentHost === "localhost" && currentPort === "3001") {
		return ENVIRONMENTS.find((env) => env.value === "local") || ENVIRONMENTS[0]
	}

	const currentUrl = window.location.origin
	return (
		ENVIRONMENTS.find((env) => env.href.startsWith(currentUrl)) ||
		ENVIRONMENTS[0]
	)
}

interface EnvironmentIconProps {
	environment: (typeof ENVIRONMENTS)[number]
	size?: "sm" | "lg"
	/** Drawn on the indigo navigation rail. */
	onRail?: boolean
	className?: string
}

function EnvironmentIcon({
	environment,
	size = "lg",
	onRail = false,
	className,
}: EnvironmentIconProps) {
	const sizeClasses = size === "sm" ? "size-6" : "size-8"
	const iconSizeClasses = size === "sm" ? "size-3.5" : "size-4"
	const roundedClass = size === "sm" ? "rounded-sm" : "rounded-lg"

	return (
		<div
			className={cn(
				"flex aspect-square items-center justify-center border",
				sizeClasses,
				roundedClass,
				getEnvironmentClasses(environment.value, onRail),
				className,
			)}
		>
			<environment.icon className={`${iconSizeClasses} shrink-0`} />
		</div>
	)
}

function EnvironmentMenuContent({
	side,
	className,
}: {
	side: "bottom" | "right"
	className?: string
}) {
	return (
		<DropdownMenuContent
			className={cn("min-w-56 rounded-lg", className)}
			align="start"
			side={side}
			sideOffset={4}
		>
			<DropdownMenuLabel className="text-xs text-muted-foreground">
				Environments
			</DropdownMenuLabel>
			{ENVIRONMENTS.map((env, index) => (
				<DropdownMenuItem key={env.value} asChild>
					<a href={env.href} className="gap-2 p-2">
						<EnvironmentIcon environment={env} size="sm" />
						{env.name}
						<DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
					</a>
				</DropdownMenuItem>
			))}
			<DropdownMenuSeparator />
			<DropdownMenuItem className="gap-2 p-2">
				<div className="flex size-6 items-center justify-center rounded-sm border bg-transparent">
					<Plus className="size-4" />
				</div>
				<div className="font-medium text-muted-foreground">Add environment</div>
			</DropdownMenuItem>
		</DropdownMenuContent>
	)
}

/** Environment switcher with the app name, for the mobile navigation sheet. */
export function EnvironmentSwitcher() {
	const { isMobile } = useSidebar()
	const activeEnvironment = getCurrentEnvironment()

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							size="lg"
							className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
						>
							<EnvironmentIcon environment={activeEnvironment} />
							<div className="grid flex-1 text-left text-sm leading-tight">
								<span className="truncate font-semibold">CardNexus Admin</span>
								<span className={`truncate text-xs ${activeEnvironment.color}`}>
									{activeEnvironment.name}
								</span>
							</div>
							<ChevronsUpDown className="ml-auto" />
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<EnvironmentMenuContent
						side={isMobile ? "bottom" : "right"}
						className="w-[--radix-dropdown-menu-trigger-width]"
					/>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	)
}

/** The current environment as a single icon at the top of the navigation rail. */
export function RailEnvironmentSwitcher() {
	const activeEnvironment = getCurrentEnvironment()

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					className="flex cursor-pointer flex-col items-center gap-1 rounded-md p-1 outline-hidden hover:bg-rail-hover focus-visible:ring-2 focus-visible:ring-white/50"
					aria-label={`CardNexus Admin, ${activeEnvironment.name}. Switch environment`}
					title={`CardNexus Admin: ${activeEnvironment.name}`}
				>
					<EnvironmentIcon environment={activeEnvironment} onRail />
				</button>
			</DropdownMenuTrigger>
			<EnvironmentMenuContent side="right" />
		</DropdownMenu>
	)
}
